<template>
  <div class="price-range">
    <div class="header">
      <div class="title">
        Selected Variants: {{ variants }}
      </div>
      <div
        v-if="list.length"
        class="price-range"
      >
        <IntlCurrency :currency="currencyData?.targetCurrency" />{{ bankersRoundWithMax(list[list.length - 1]?.price * currencyData.rate, 0.01) }}
        -
        <IntlCurrency :currency="currencyData?.targetCurrency" />{{ bankersRoundWithMax(list[0]?.price * currencyData.rate, 0.01) }}
        <span class="suffix"> / Piece</span>
      </div>
    </div>

    <div class="range-list">
      <!-- 左侧固定 -->
      <div class="fixed-column">
        <div class="cell">
          Quantity
        </div>
        <div class="cell cell-item-content">
          Item
        </div>
        <div class="cell large">
          {{ shippingConfirmed ? 'PIFP' : 'Est.PIFP' }}
        </div>
      </div>

      <!-- 滚动区域 -->
      <div
        ref="scrollRef"
        class="scroll-container"
        @scroll="onScroll"
      >
        <div
          :style="{ width: list.length ? '' : '100%' }"
          class="scroll-inner"
        >
          <div
            v-for="(item, index) in list"
            :key="index"
            :class="['range-item', { active: index === activeIndex }]"
          >
            <img
              v-if="index === 1 && activeIndex === 1"
              class="choices-icon"
              src="@/assets/illus/illus-price-choices.svg"
              alt="active"
            >
            <div class="cell">
              {{ item.startQuantity }} Piece+
            </div>
            <div class="cell cell-item-content">
              <IntlCurrency :currency="currencyData?.targetCurrency" />
              {{ bankersRoundWithMax(item.price * currencyData?.rate, 0.01) }}
            </div>
            <div class="cell large">
              <IntlCurrency :currency="currencyData?.targetCurrency" />
              {{ formatPifp(item) }}
            </div>
          </div>
        </div>
      </div>

      <!-- 滚动按钮 -->
      <div
        class="scroll-button"
        @click="scrollToggle"
        v-if="isScrollable"
      >
        <Icon
          :class="{ rotate: isAtEnd }"
          class="icon"
          name="arrow-right-l-2"
        />
      </div>
      <!-- 安全区域 -->
      <div class="safe-offset" />
    </div>
    <div class="pifp-tips">
      <div class="text">
        {{
          shippingConfirmed ?
            '*Per-item Fulfillment Price(PIFP). Calculated based on the minimum length, width, height and weight only. Please select the product you want to view the actual fulfillment price.'
            : '*Estimation Per-item Fulfillment Price(Est.PIFP). The length, width, height and weight are calculated by predicting the average value of similar products. The price will be verified again after adding it to the shopping cart. Please wait patiently.'
        }}
      </div>
      <Tooltip :offset="4">
        <template #content>
          <div style="width: 350px;">
            {{
              shippingConfirmed ?
                'Per-item Fulfillment Price(PIFP). Calculated based on the minimum length, width, height and weight only. Please select the product you want to view the actual fulfillment price.'
                : 'Estimation Per-item Fulfillment Price(Est.PIFP). The length, width, height and weight are calculated by predicting the average value of similar products. The price will be verified again after adding it to the shopping cart. Please wait patiently.'
            }}
          </div>
        </template>
        <Icon
          class="icon"
          name="icon-help-tip-l"
        />
      </Tooltip>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import Icon from '@/components/Icon/Icon.vue'
import { IntlCurrency } from '@/components/NewIntlCurrency'
import { bankersRoundWithMax } from '@/utils/utils'
import { Tooltip } from '@/components/Tooltip'
import { useCurrencyStore } from '@/pinia/currency'
import { useWindowSize } from '@/hooks/useWindowSize'
import { useCalcShip } from '@/views/OfferView/components/hooks/useCalcShip'

const props = defineProps({
  list: {
    type: Array,
    default: () => []
  },
  variants: {
    type: String,
    default: ''
  }
})

const { selectedProductCount, shippingConfirmed } = useCalcShip()

const currencyStore = useCurrencyStore()
const { width } = useWindowSize()

const activeIndex = ref(1)
const isAtEnd = ref(false)
const scrollRef = ref(null)

const isScrollable = ref(false)

/**
 * 判断容器是否可滚动，若可滚动才显示滚动按钮
 */
watch(() => props.list, () => {
  if (scrollRef.value == null) return
  const el = scrollRef.value
  isScrollable.value = el.scrollWidth > el.clientWidth
}, {
  flush: 'post'
})

watch(width, () => {
  if (scrollRef.value == null) return
  const el = scrollRef.value
  isScrollable.value = el.scrollWidth > el.clientWidth
})

const currencyData = computed(() => currencyStore.current)

watch(selectedProductCount, (value) => {
  if (value) {
    setActive(value)
  } else {
    activeIndex.value = props.list.length > 1 ? 1 : 0
  }
})

watch(() => props.list, (list) => {
  if (list.length > 1) {
    setActive(selectedProductCount.value)
  } else {
    activeIndex.value = 0
  }
})

const formatPifp = (item) => {
  const targetPrice = +bankersRoundWithMax(item.price * currencyData.value.rate, 0.01)
  const v = (item.shipAmount + targetPrice * item.startQuantity) / item.startQuantity
  return v.toFixed(2)
}

const onScroll = () => {
  const el = scrollRef.value
  isAtEnd.value = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1
}

const scrollToggle = () => {
  const el = scrollRef.value
  el.scrollTo({
    left: isAtEnd.value ? 0 : el.scrollWidth,
    behavior: 'smooth'
  })
}

const setActive = (total) => {
  const el = scrollRef.value

  /**
   * 不满足最小起订量的情况下，选中第一个价格区间
   */
  activeIndex.value = 0
  props.list.forEach((item, index) => {
    if (total >= item.startQuantity) {
      activeIndex.value = index
      if (index === 0) {
        el.scrollTo({
          left: 0,
          behavior: 'smooth'
        })
      }
      if (index > 2) {
        el.scrollTo({
          left: el.scrollWidth,
          behavior: 'smooth'
        })
      }
    }
  })
}

onMounted(() => {
  scrollRef.value.scrollLeft = scrollRef.value.scrollWidth
})

defineExpose({
  activeIndex
})
</script>

<style scoped lang="less">
.price-range {
  margin-top: 16px;

  .header {
    .title {
      color: #232323;
      font-size: 12px;
      font-family: mon-500;
    }
    .price-range {
      color: #232323;
      font-size: 16px;
      font-family: mon-700;
      margin-top: 8px;
      .suffix {
        color: #6e717a;
        font-size: 10px;
        font-family: mon-500;
      }
    }
  }

  .title {
    font-size: 14px;
    color: #232323;
    font-family: mon-700;

    .suffix {
      font-size: 10px;
      color: #6e717a;
      font-family: mon-500;
    }
  }

  .range-list {
    position: relative;
    display: flex;
    align-items: center;
    margin-top: 6px;

    &::before,
    &::after {
      content: '';
      position: absolute;
      left: 0;
      right: 0;
      height: 0.5px;
      background-color: #dfdfdf;
      z-index: 9;
    }

    &::before {
      top: 48px;
    }

    &::after {
      top: 78px;
    }

    .safe-offset {
      position: absolute;
      right: 0;
      width: 4px;
      height: 104px;
      background-color: #eff4fd;
      z-index: -1;
      border-radius: 0 4px 4px 0;
    }

    .fixed-column {
      flex-shrink: 0;
      width: 20%;
      min-width: 72px;
      max-width: 96px;
      padding: 4px;
      box-shadow: 6px 0 10px -4px rgba(0, 0, 0, 0.1);
      background: #eff4fd;
      border-radius: 4px 0 0 4px;
      position: relative;
      z-index: 2;

      .cell {
        height: 30px;
        font-size: 10px;
        font-family: mon-500;
        color: #232323;
        display: flex;
        align-items: center;
        justify-content: center;
        &.cell-item-content {
          font-size: 11px;
        }
        &.large {
          height: 36px; font-size: 11px;
          .icon {
            margin-left: 2px;
            cursor: pointer;
          }
        }
      }
    }

    .scroll-container {
      flex: 1;
      height: 132px;
      overflow-x: auto;
      overflow-y: hidden;
      position: relative;
      display: flex;
      align-items: center;

      &::-webkit-scrollbar {
        display: none;
      }

      .scroll-inner {
        display: flex;
        position: relative;
        overflow: visible;
        height: 104px;
        width: 100%;
        background: #eff4fd;
        padding: 0 4px;
      }

      .range-item {
        flex: 1 0 80px;
        display: flex;
        align-items: center;
        flex-direction: column;
        position: relative;
        width: 80px;
        height: 104px;
        padding: 4px;
        background: #eff4fd;

        .cell {
          width: 64px;
          height: 30px;
          font-size: 10px;
          font-family: mon-500;
          color: #232323;
          display: flex;
          align-items: center;
          justify-content: center;
          &.cell-item-content {
            font-size: 11px;
            font-family: mon-600;
          }
          &.large {
            height: 36px;
            font-size: 12px;
            font-family: mon-700;
          }
        }

        &.active {
          outline: 2px solid #052784;
          border-radius: 6px;
          z-index: 10;
          .cell {
            color: #052784;
          }
        }

        .choices-icon {
          position: absolute;
          top: -8px;
          left: 50%;
          transform: translateX(-50%);
        }
      }
    }

    .scroll-button {
      position: absolute;
      top: 42%;
      right: -10px;
      width: 20px;
      height: 20px;
      border-radius: 50%;
      background: white;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.04);
      z-index: 99;
      cursor: pointer;

      .icon {
        font-size: 12px;
        transition: transform 0.3s ease;
      }

      .rotate {
        transform: rotate(180deg);
      }
    }
  }

  .pifp-tips {
    display: flex;
    align-items: center;
    margin-top: -4px;
    .text {
      flex: 1;
      color: #6e717a;
      font-size: 11px;
      font-family: mon-500;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .icon {
      font-size: 16px;
      color: #232323;
      cursor: pointer;
    }
  }
}
</style>
